import { useMutation, useQuery } from '@apollo/client';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  Appetite_Model_Enum,
  Risk_Scoring_Model_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetModulesDocument,
  Parent_Type_Enum,
  ResetTabPreferencesDocument,
  UpdateAggregationSettingsForOrgDocument,
  UpdateModulesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import { useEffect } from 'react';
import { create } from 'zustand';

import { evictField } from '@/utils/graphqlUtils';

export type BasicModule = {
  enabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: any;
  subModules?: Record<string, BasicModule>;
  allowTabConfig?: boolean;
};

export type Modules = {
  toggle: (id: string) => void;
  isModuleEnabled: (id: string) => boolean;
  setConfig: (id: string, config: Record<string, unknown>) => void;
  modules: Record<string, BasicModule>;
  isDirty: boolean;
  setIsDirty: (isDirty: boolean) => void;
  dirtyModules?: string[];
};

/**
 * Warning! These are just the default modules. If a customer has a different set of modules, they will be fetched from the server.
 * If you want to add a new module that's on by default, don't forget to migrate existing customer settings to the new format.
 *
 * Other gotchas:
 * - If the module or submodule allows tab configuration, the key / module id _must_ be the same as the Parent Type.
 * - When adding a new module, ensure that the useNavItems hook is also updated to include the new module.
 * - When adding a new module, don't forget to update the translation files for the module titles and descriptions.
 */
const defaultModules: Record<string, BasicModule> = {
  risk: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      impact: {
        enabled: false,
      },
      risk_scoring: {
        enabled: false,
      },
      appetite: {
        enabled: true,
      },
      appetite_cascading: {
        enabled: false,
      },
      acceptance: {
        enabled: true,
      },
      rcsa_wizard: {
        enabled: false,
      },
    },
  },
  document: {
    enabled: false,
    allowTabConfig: true,
    subModules: {
      attestation: {
        enabled: false,
      },
      public_document: {
        enabled: true,
      },
    },
  },
  obligation: {
    enabled: false,
    allowTabConfig: true,
    subModules: {
      compliance_monitoring_assessment: {
        enabled: false,
        allowTabConfig: true,
      },
      reg_feed: {
        enabled: false,
      },
    },
  },
  third_party: {
    enabled: false,
    allowTabConfig: true,
  },
  internal_audit_entity: {
    enabled: false,
    allowTabConfig: true,
    subModules: {
      internal_audit_report: {
        enabled: true,
        allowTabConfig: true,
      },
    },
  },
  issue: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      cause: {
        enabled: true,
      },
      consequence: {
        enabled: true,
      },
    },
  },
  control: {
    enabled: true,
    allowTabConfig: true,
    subModules: {
      control_group: {
        enabled: true,
      },
    },
  },
  action: {
    enabled: true,
    allowTabConfig: true,
  },
  indicator: {
    enabled: true,
    allowTabConfig: false,
  },
  assessment: {
    enabled: true,
    allowTabConfig: true,
  },
  incident_reporting: {
    enabled: true,
  },
  approval: {
    enabled: false,
  },
  custom_datasource: {
    enabled: false,
  },
  notification: {
    enabled: true,
  },
  enterprise_risk: {
    enabled: false,
    allowTabConfig: true,
  },
  ai: {
    enabled: false,
    subModules: {
      chat: {
        enabled: false,
      },
      chat_warning: {
        enabled: false,
      },
      suggested_controls: {
        enabled: false,
      },
    },
  },
};

/**
 * Set of module IDs that correspond to valid Parent_Type_Enum values.
 * Only these modules can have their tab preferences reset.
 */
const PARENT_TYPE_MODULES = new Set<Parent_Type_Enum>(
  Object.values(Parent_Type_Enum)
);

const isParentTypeEnum = (value: string): value is Parent_Type_Enum => {
  return PARENT_TYPE_MODULES.has(value as Parent_Type_Enum);
};

export const useModulesStore = create<Modules>((set, get) => {
  return {
    toggle: (id: string) => {
      set((state) => {
        const updatedModules = _.cloneDeep(state.modules);

        if (_.has(updatedModules, id)) {
          const currentEnabled = _.get(updatedModules, `${id}.enabled`, false);
          const newEnabled = !currentEnabled;

          _.set(updatedModules, `${id}.enabled`, newEnabled);

          // If disabling a module, also disable all its submodules
          if (!newEnabled) {
            const module = _.get(updatedModules, id) as BasicModule | undefined;
            if (module?.subModules) {
              Object.keys(module.subModules).forEach((subModuleKey) => {
                _.set(
                  updatedModules,
                  `${id}.subModules.${subModuleKey}.enabled`,
                  false
                );
              });
            }
          }
        } else {
          console.warn(`Module with id "${id}" does not exist.`);
        }

        const mainModuleId = id.split('.')[0];

        return {
          modules: updatedModules,
          isDirty: true,
          dirtyModules: _.uniq(
            _.concat(state.dirtyModules ?? [], mainModuleId)
          ),
        };
      });
    },
    setConfig: (id: string, config: Record<string, unknown>) => {
      set((state) => {
        const updatedModules = { ...state.modules };
        if (_.has(updatedModules, id)) {
          _.set(updatedModules, `${id}.config`, config);
        } else {
          console.warn(`Module with id "${id}" does not exist.`);
        }

        return { modules: updatedModules, isDirty: true };
      });
    },
    isModuleEnabled: (id: string): boolean => {
      const state = get();

      return _.get(state.modules, `${id}.enabled`, false) as boolean;
    },
    modules: defaultModules,
    dirtyModules: [],
    // @TODO: Obsolete, can be inferred from dirtyModules
    isDirty: false,
    setIsDirty: (isDirty: boolean) => {
      set({ isDirty, dirtyModules: isDirty ? get().dirtyModules : [] });
    },
  };
});

export const useModules = () => {
  const {
    toggle,
    isModuleEnabled,
    modules,
    setConfig,
    isDirty,
    setIsDirty,
    dirtyModules,
  } = useModulesStore();
  const { addNotification } = useNotifications();
  const [updateAggregationSettings] = useMutation(
    UpdateAggregationSettingsForOrgDocument
  );
  const [updateModules, { loading: updateLoading }] = useMutation(
    UpdateModulesDocument,
    {
      onCompleted: () => {
        addNotification({
          type: 'success',
          content: 'Modules updated successfully.',
        });
      },
      update: (cache) => {
        evictField(cache, 'organisation_module');
      },
    }
  );
  const { data, loading } = useQuery(GetModulesDocument);
  const [resetTabs] = useMutation(ResetTabPreferencesDocument, {
    update: (cache) => {
      evictField(cache, 'organisation_tab_preference');
      evictField(cache, 'user_tab_preference');
    },
  });

  const commit = async (config: {
    RiskScoringModel: Risk_Scoring_Model_Enum;
    RiskScoringModelConfig: string;
    AppetiteCascadingModel: Appetite_Model_Enum;
    AppetiteCascadingModelConfig: string;
  }) => {
    await updateModules({
      variables: { ModuleSettings: modules },
    });

    if (dirtyModules?.length) {
      await Promise.all(
        dirtyModules
          .filter((c) => isParentTypeEnum(c))
          .map(async (moduleId: Parent_Type_Enum) => {
            return await resetTabs({
              variables: {
                ObjectType: moduleId,
              },
            });
          })
      );
    }

    const riskScoringEnabled = modules.risk?.subModules?.risk_scoring?.enabled;
    const appetiteCascadingEnabled =
      modules.risk?.subModules?.appetite_cascading?.enabled;

    if (riskScoringEnabled || appetiteCascadingEnabled) {
      await updateAggregationSettings({
        variables: {
          RiskScoringModel:
            riskScoringEnabled && config.RiskScoringModel
              ? config.RiskScoringModel
              : null,
          AppetiteCascadingModel:
            appetiteCascadingEnabled && config.AppetiteCascadingModel
              ? config.AppetiteCascadingModel
              : null,
          Config: {
            ...JSON.parse(
              riskScoringEnabled ? config.RiskScoringModelConfig : '{}'
            ),
            ...JSON.parse(
              appetiteCascadingEnabled
                ? config.AppetiteCascadingModelConfig
                : '{}'
            ),
          },
        },
      });
    } else {
      await updateAggregationSettings({
        variables: {
          RiskScoringModel: null,
          AppetiteCascadingModel: null,
          Config: {},
        },
      });
    }

    setIsDirty(false);
  };

  const reset = () => {
    if (data?.organisation_module?.[0]?.ModuleSettings && !loading) {
      useModulesStore.setState({
        modules: _.merge(
          {},
          _.cloneDeep(defaultModules),
          _.cloneDeep(
            data.organisation_module[0].ModuleSettings as Modules['modules']
          )
        ),
        isDirty: false,
      });
      addNotification({
        type: 'success',
        content: 'Changes reverted.',
      });
    }
  };

  useEffect(() => {
    if (data?.organisation_module?.[0]?.ModuleSettings && !loading) {
      useModulesStore.setState({
        modules: _.merge(
          {},
          _.cloneDeep(defaultModules),
          _.cloneDeep(
            data.organisation_module[0].ModuleSettings as Modules['modules']
          )
        ),
      });
    }
  }, [loading, data]);

  return {
    toggle,
    isModuleEnabled,
    modules,
    commit,
    reset,
    loading: updateLoading || loading,
    setConfig,
    isDirty,
  };
};
